import { AuthProvider } from 'react-admin'
import { loginUser, logout, loginByPinCode, checkInUser } from '@api'
import { setAuth, getUserName, getAuthToken, getTabSessionToken } from './common/auth'
import { queryClient } from './common/queryClient'
import { clearSession } from './common/session'
import { setPermissions } from '@store/reducers/auth'
import { setUserData, selectedGroup } from '@reducer/profile'
import storeRef from '@store/storeRef'
import { UserITF } from '@/types'

export interface PermissionITF {
  GlobalManageCustomers?: boolean
  GlobalManageRoles?: boolean
  GlobalManageFeatureFlags?: boolean
  GlobalManageSystemSettings?: boolean
  EditMappingHub?: boolean
  ManageMappingHub?: boolean
  EditPcaHub?: boolean
  ManagePcaHub?: boolean
  ManageEntities: boolean
  ViewAnalytics: boolean
  ViewCompanyAllBuildings: boolean
  PortfolioTeam?: boolean
  [key: string]: boolean | undefined
}

export const PERMISSION_DEFAULTS: PermissionITF = {
  GlobalManageCustomers: false,
  ManageEntities: false,
  ViewAnalytics: false,
  ViewCompanyAllBuildings: false,
}
interface Login {
  username?: string
  password?: string
  type?: 'email' | 'pin'
  [key: string]: string | undefined
}

const avatarmap = {
  admin:
    'https://avataaars.io/?avatarStyle=Circle&topType=LongHairStraight&accessoriesType=Blank&hairColor=BrownDark&facialHairType=Blank&clotheType=BlazerShirt&eyeType=Default&eyebrowType=Default&mouthType=Default&skinColor=Light',
  'Sterling Bay':
    'https://avataaars.io/?avatarStyle=Circle&topType=WinterHat2&accessoriesType=Kurt&hatColor=Blue01&facialHairType=Blank&clotheType=ShirtVNeck&clotheColor=Gray01&eyeType=WinkWacky&eyebrowType=FlatNatural&mouthType=Serious&skinColor=Yellow',
  'CA Ventures':
    'https://avataaars.io/?avatarStyle=Transparent&topType=ShortHairShortRound&accessoriesType=Sunglasses&hairColor=SilverGray&facialHairType=BeardMajestic&facialHairColor=BrownDark&clotheType=CollarSweater&clotheColor=Pink&eyeType=Surprised&eyebrowType=UpDown&mouthType=Sad&skinColor=Pale',
  Zeller:
    'https://avataaars.io/?avatarStyle=Transparent&topType=LongHairShavedSides&accessoriesType=Kurt&hairColor=Auburn&facialHairType=Blank&facialHairColor=Blonde&clotheType=GraphicShirt&clotheColor=Blue02&graphicType=Skull&eyeType=Dizzy&eyebrowType=UpDownNatural&mouthType=Eating&skinColor=Yellow',
  'Friedman Properties':
    'https://avataaars.io/?avatarStyle=Transparent&topType=WinterHat4&accessoriesType=Wayfarers&hatColor=Blue03&facialHairType=MoustacheMagnum&facialHairColor=Red&clotheType=CollarSweater&clotheColor=White&graphicType=Pizza&eyeType=Close&eyebrowType=RaisedExcited&mouthType=ScreamOpen&skinColor=DarkBrown',
  Snatch:
    'https://avataaars.io/?avatarStyle=Transparent&topType=LongHairNotTooLong&accessoriesType=Wayfarers&hairColor=Auburn&facialHairType=BeardLight&facialHairColor=Black&clotheType=Hoodie&clotheColor=Pink&eyeType=WinkWacky&eyebrowType=Angry&mouthType=Sad&skinColor=Yellow',
  '3 Idiots':
    'https://avataaars.io/?avatarStyle=Transparent&topType=Hijab&accessoriesType=Blank&hatColor=Blue02&hairColor=Auburn&facialHairType=BeardLight&facialHairColor=BrownDark&clotheType=BlazerShirt&clotheColor=White&eyeType=Default&eyebrowType=RaisedExcited&mouthType=Tongue&skinColor=Light',
  'Monty Python and the Holy Grail':
    'https://avataaars.io/?avatarStyle=Transparent&topType=LongHairStraight2&accessoriesType=Kurt&hatColor=Gray02&hairColor=SilverGray&facialHairType=MoustacheMagnum&facialHairColor=BlondeGolden&clotheType=CollarSweater&clotheColor=Black&eyeType=Happy&eyebrowType=FlatNatural&mouthType=ScreamOpen&skinColor=Tanned',
}

const mockUser = {
  global: { UserName: 'freddie@activate.net', Password: 'Activate2022' },
  executive: { UserName: 'tony.stark@sterlingbay.com', Password: 'SterlingBay2022' },
  admin: { UserName: 'bw@sterlingbay.com', Password: 'SterlingBay2022' },
  manager: { UserName: 'stbm1@sterlingba.com', Password: 'SterlingBay2022' },
  manager2: { UserName: 'tom@sterlingbay.com', Password: 'SterlingBay2022' },
  manager3: { UserName: 'jz@zeller.com', Password: 'Zeller2022' },
}
// Rebuilds Redux session state for a tab that holds a valid token but an
// empty auth/profile slice (a tab opened after another tab logged in, one that
// rehydrated a pre-login persist snapshot, or a session entered via
// impersonation redeem). checkAuth fires this instead of rejecting — rejecting
// made react-admin auto-run logout(), archiving a live token server-side. A
// missing profile.user must be recovered too, not just permissions: the layout
// renders <Loading/> unconditionally on user == null (AppBarExtend, Dashboard)
// and no other code path can repopulate it, so token-without-user was an
// eternal loading screen with the login form unreachable. In-flight guard
// because checkAuth runs on every guarded route mount.
let sessionRecovery: Promise<void> | null = null
const recoverSession = () => {
  if (sessionRecovery) return sessionRecovery
  // Send the persisted group selection so recovery lands in the user's actual
  // context — an empty body makes the backend fall back to the token's last
  // group, which may not match what this tab is showing.
  const sentGroupId = storeRef.current?.getState()?.profile?.selectedGroupId || undefined
  sessionRecovery = checkInUser<UserITF>({ GroupId: sentGroupId })
    .then((response) => {
      const hash = response?.UserContext?.PermissionsHash
      if (!hash) return
      const permissions: PermissionITF = { ...PERMISSION_DEFAULTS, ...hash, PortfolioTeam: !!response.PortfolioTeam }
      storeRef.current?.dispatch(setPermissions(permissions))
      const profile = storeRef.current?.getState()?.profile
      if (!profile?.user) {
        storeRef.current?.dispatch(setUserData(response))
      }
      // Mirror Login's post-login bootstrap: non-global users operate inside
      // their current group, and the group-scoped pages assume selectedGroupId
      // is populated. CurrentGroupName is a lang map; outside a component there
      // is no locale hook, so fall back to en.
      const context = response.UserContext
      // Re-sync when the backend resolved a different group than we sent: that
      // means the persisted selection is stale (deleted group / poisoned
      // state) and the server fell back — keeping the dead id would replay the
      // failure on every subsequent CheckIn.
      const staleSelection = !!(sentGroupId && context?.CurrentGroupId && context.CurrentGroupId !== sentGroupId)
      if ((!profile?.selectedGroupId || staleSelection) && !hash.GlobalManageCustomers && context?.CurrentGroupId) {
        storeRef.current?.dispatch(
          selectedGroup({
            groupId: context.CurrentGroupId,
            groupName: context.CurrentGroupName?.en,
            group: {
              acId: context.CurrentGroupId,
              GroupName: context.CurrentGroupName,
              LogoFilename: context.CurrentGroupLogoFilename,
            },
          })
        )
      }
    })
    .catch((error) => {
      // A dead token can never recover, and while it sits in localStorage the
      // login form is unreachable (Login redirects to '/' whenever a token
      // exists). Tear the session down for the same fatal codes checkError
      // treats as session-ending; swallow anything transient — the next
      // checkAuth (any route mount) retries.
      const code = String(error?.body?.error?.code ?? error?.message ?? '')
      // Only an actual auth error code proves the token is dead. A bare
      // 401/403 with no code (edge/WAF/shed-load response, HTML body) is
      // infrastructure noise — tearing the session down on it kicked users
      // whose requests merely got dropped upstream. Transient errors are
      // retried by the next checkAuth (any route mount).
      if (code.startsWith('usr.aut') || code.startsWith('auth.token')) {
        clearSession(queryClient)
        window.location.assign('/login')
      }
    })
    .finally(() => {
      sessionRecovery = null
    })
  return sessionRecovery
}

const authProvider: AuthProvider = {
  login: async (params: Login) => {
    const { username = '', password = '', type = 'email', ...other } = params
    let response = {} as UserITF
    if (type === 'email') {
      let name = username
      let Password = password
      if (import.meta.env.VITE_APP_TYPE !== 'PRODUCTION') {
        if (mockUser[username]) {
          name = mockUser[username].UserName
          Password = mockUser[username].Password
        }
      }
      response = await loginUser<UserITF>({ UserName: name, Password })
    }
    if (type === 'pin') {
      response = await loginByPinCode<UserITF>({
        ...other,
      })
    }

    const {
      acId,
      UserName,
      UserToken,
      PortfolioTeam,
      UserContext: { PermissionsHash },
    } = response
    const permissions: PermissionITF = { ...PERMISSION_DEFAULTS, ...PermissionsHash, PortfolioTeam: !!PortfolioTeam }
    // Drop any cached queries from a prior session before this one renders. The
    // React Query cache key omits the user token, so a stale list (e.g. an old
    // portfolioTeam result) would otherwise be served to the new session.
    queryClient.clear()
    setAuth(UserToken, UserName)
    storeRef.current?.dispatch(setPermissions(permissions))
    // return Promise.resolve({ redirectTo: '/' })
    return Promise.resolve(response)
    // return Promise.resolve('/adminsearch');
    // return tfsAuthProvider.login().then(async () => {
    //     const result = await fetch(
    //       process.env.REACT_APP_BASE_URL +
    //         "/users/permissions/" +
    //         tfsAuthProvider.getAccount().userName
    //     );

    //     const body = await result.json();
    //     localStorage.setItem("permissions", body);
    //     return Promise.resolve();
    // });
  },
  logout: async () => {
    const token = getAuthToken()
    // localStorage is shared across tabs: if the token there is no longer the
    // one this tab's session was built on, another tab has since logged in.
    // An auto-logout here (e.g. checkError on this tab's dead session) would
    // send the OTHER tab's fresh token to User/Logout and archive it server-
    // side. Reload into the newer session instead of tearing it down.
    const sessionToken = getTabSessionToken()
    if (token && sessionToken && token !== sessionToken) {
      window.location.reload()
      return new Promise<void>(() => {})
    }
    if (token) {
      try {
        await logout({})
      } catch {
        // The token may already be archived server-side (usr.aut.iut) — the
        // backend call fails, but the local session must still be cleared.
      }
    }
    // Full teardown even without a token: persisted redux state (permissions)
    // can outlive it. Clears token, impersonation, redux state incl. the
    // persisted copy, the permissions-version tracker, and the React Query
    // cache — the cache key is not scoped to the user token, so the next
    // session could otherwise reuse this user's data.
    clearSession(queryClient)
    return Promise.resolve()
    // return Promise.reject({ redirectTo: '/credentials-required' });
  },
  checkError: (error) => {
    const message = String(error?.message ?? '')
    // New error contract carries the code in the JSON body ({ error: { code } },
    // kept on HttpError/axios rejections as .body); fall back to the message,
    // which both http layers set to the code.
    const code = String(error?.body?.error?.code ?? message)
    // Non-fatal denials under the new contract: a missing permission or a
    // disabled feature (HTTP 403) must not tear down the whole session.
    if (code === 'auth.permission.denied' || code.startsWith('auth.feature.disabled')) {
      return Promise.resolve()
    }
    // Dead/invalidated token (e.g. a user archived by the dedup datafix, now
    // rejected by the backend TokenStatus check). Reject so React Admin runs
    // logout() — which clears the token, permissions, and the query cache — and
    // redirects to the login page instead of leaving a broken session.
    // Require an actual auth error code: the backend always attaches one to
    // real 401/403s (both contracts), so a bare status with no code is an
    // edge/WAF/shed-load response — logging out on it archives a LIVE token
    // (logout POSTs User/Logout) and kicks every tab. Treat those as transient.
    if (code.startsWith('usr.aut') || code.startsWith('auth.token')) {
      return Promise.reject()
    }
    return Promise.resolve()
  },
  checkAuth: () => {
    if (window.location.pathname.includes('pdfProofReport')) return Promise.resolve()
    // The token is the only proof of a session. Redux permissions and
    // profile.user can lag it (fresh tab sharing a logged-in localStorage
    // token, persist rehydration, impersonation redeem) — rejecting on that
    // gap made react-admin auto-run logout(), which archived the live token
    // server-side and bounced the user to login. Recover the session state in
    // the background instead.
    if (!getAuthToken()) return Promise.reject({ message: false })
    const state = storeRef.current?.getState()
    if (!state?.auth?.permissions || !state?.profile?.user) recoverSession()
    // A fully-persisted session can still carry an empty group selection
    // (legacy clearGroup blobs). Non-global users always operate inside a
    // group, so recover and let the server re-point it; global admins browse
    // cross-group with no selection legitimately.
    else if (!state.profile.selectedGroupId && !state.auth.permissions.GlobalManageCustomers) recoverSession()
    return Promise.resolve()
  },
  getPermissions: () => {
    const perms = storeRef.current?.getState()?.auth?.permissions
    return Promise.resolve(perms)
  },
  getIdentity: () => {
    try {
      const propertyName: string = getUserName()
      return Promise.resolve({
        id: 'user',
        fullName: propertyName,
        avatar: avatarmap.admin,
        //'data:image/jpeg;base64,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',
      })
    } catch (error) {
      return Promise.reject(error)
    }
  },
}

export default authProvider
